import { useEffect, useRef, useState } from "react";
import Navbar from "@/components/Navbar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { UploadCloud, Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";

const STATUS_META = {
  pending: { label: "Pending review", icon: Clock, cls: "text-[#52525B]" },
  approved: { label: "Approved", icon: CheckCircle2, cls: "text-green-700" },
  rejected: { label: "Rejected", icon: XCircle, cls: "text-red-700" },
};

export default function FeedbackVideo() {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [duration, setDuration] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [mine, setMine] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);
  const videoRef = useRef(null);

  const loadMine = () => {
    setLoadingMine(true);
    api.get("/feedback-video/mine").then((r) => setMine(r.data)).finally(() => setLoadingMine(false));
  };
  useEffect(() => { loadMine(); }, []);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setDuration(null);
    const url = URL.createObjectURL(f);
    setPreviewUrl(url);
  };

  const onPreviewLoaded = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const upload = async () => {
    if (!file) { toast.error("Choose a video first"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (caption.trim()) form.append("caption", caption.trim());
      await api.post("/feedback-video/upload", form);
      toast.success("Thanks! Your video is in for review.");
      setFile(null);
      setPreviewUrl(null);
      setDuration(null);
      setCaption("");
      loadMine();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const durationLooksOff = duration != null && (duration < 1.2 || duration > 15.0);

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-10">
        <span className="label-mono">Share Your Story</span>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter mt-2">Feedback Video</h1>
        <p className="text-[#52525B] mt-2">
          Record a short clip (up to 15 seconds) about your experience. Our team reviews every
          submission before anything gets shared publicly.
        </p>

        <div className="mt-8 card-brutal p-6">
          {previewUrl ? (
            <div className="space-y-4">
              <video
                ref={videoRef}
                src={previewUrl}
                controls
                onLoadedMetadata={onPreviewLoaded}
                className="w-full rounded-xl border-2 border-[#0A0A0A] bg-black max-h-80"
              />
              {duration != null && (
                <div className={`text-sm font-semibold ${durationLooksOff ? "text-red-700" : "text-green-700"}`}>
                  Length: {duration.toFixed(1)}s {durationLooksOff && "— must be between 1.5 and 15 seconds"}
                </div>
              )}
              <button
                onClick={() => { setFile(null); setPreviewUrl(null); setDuration(null); }}
                className="text-sm font-semibold underline"
              >
                Choose a different video
              </button>
            </div>
          ) : (
            <label
              data-testid="feedback-video-dropzone"
              className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-[#0A0A0A] rounded-xl py-14 cursor-pointer hover:bg-[#FAFAF9]"
            >
              <UploadCloud size={28} strokeWidth={2.5} />
              <div className="font-semibold">Tap to choose a video</div>
              <div className="text-xs text-[#52525B]">MP4, MOV or WebM · up to 15 seconds · under 18MB</div>
              <input data-testid="feedback-video-input" type="file" accept="video/*" onChange={onFileChange} className="hidden" />
            </label>
          )}

          <div className="mt-5">
            <label className="label-mono block mb-2">Caption (optional)</label>
            <input
              data-testid="feedback-video-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              maxLength={280}
              placeholder="What made your experience great?"
              className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            onClick={upload}
            disabled={!file || uploading}
            data-testid="feedback-video-submit"
            className="btn-brutal bg-blue-600 text-white px-5 py-3 text-sm w-full mt-5 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="animate-spin" size={16} /> : <UploadCloud size={16} strokeWidth={2.5} />}
            Submit for review
          </button>
        </div>

        <div className="mt-10">
          <span className="label-mono">Your submissions</span>
          {loadingMine ? (
            <div className="mt-3 text-sm text-[#52525B]">Loading…</div>
          ) : mine.length === 0 ? (
            <div className="mt-3 text-sm text-[#52525B]">Nothing submitted yet.</div>
          ) : (
            <div className="mt-3 space-y-3">
              {mine.map((v) => {
                const meta = STATUS_META[v.status] || STATUS_META.pending;
                const Icon = meta.icon;
                return (
                  <div key={v.id} data-testid={`my-feedback-video-${v.id}`} className="card-brutal p-4 flex items-center gap-4">
                    <video src={v.video_url} className="w-20 h-20 object-cover rounded-lg border-2 border-[#0A0A0A] bg-black" muted />
                    <div className="flex-1 min-w-0">
                      <div className={`inline-flex items-center gap-1 text-sm font-semibold ${meta.cls}`}>
                        <Icon size={14} strokeWidth={2.5} /> {meta.label}
                      </div>
                      <div className="text-xs text-[#52525B] mt-1">{format(new Date(v.created_at), "dd MMM yyyy")} · {v.duration_seconds}s</div>
                      {v.caption && <div className="text-sm mt-1 truncate">{v.caption}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
